<script setup lang="ts">
interface Product {
  id: string
  handle: string
  name: string
  category: string
  price: string
  images: Array<{ url: string; altText: string | null }>
  tags: string[]
}

// ── Mosaic content ──
const QUOTES = [
  { text: 'Made for the long haul. Worn in, not worn out.', attribution: 'Wet Water' },
  { text: 'We favor natural materials, aging finishes, and makers who take their time.', attribution: 'Wet Water' },
  { text: 'The kind of thing you reach for every day without thinking.', attribution: 'Wet Water' },
  { text: 'Not trend. Not season. Just the right thing.', attribution: 'Wet Water' },
  { text: 'Still water runs deep. So does good craft.', attribution: 'Wet Water' },
] as const

const EDITORIALS = [
  { eyebrow: 'On Materials', copy: 'Each piece is chosen for how it ages, not how it arrives.', cta: null },
  { eyebrow: 'On Making', copy: 'We work with makers who understand that slow is not inefficient — it\u2019s intentional.', cta: null },
  { eyebrow: 'From the Journal', copy: null, cta: { label: 'Read the Story \u2192', href: '/journal' } },
] as const

type MosaicSlot =
  | { type: 'product'; product: Product; gridColumn: string; gridRow: string; colspan: 2 | 4; dark?: boolean }
  | { type: 'quote'; quote: typeof QUOTES[number]; gridColumn: string; gridRow: string }
  | { type: 'editorial'; editorial: typeof EDITORIALS[number]; gridColumn: string; gridRow: string }
  | { type: 'image'; url: string; altText: string; gridColumn: string; gridRow: string }
  | { type: 'banner'; category: string; count: number; bgImage: string; gridColumn: string; gridRow: string }
  | { type: 'wavy-rule'; label: string; gridColumn: string; gridRow: string }

  
// ── Data ──
const { data: allProducts } = await useFetch<Product[]>('/api/products')
const products = computed(() => allProducts.value ?? [])

// ── Category filter (supports ?category= query param from footer links) ──
const route = useRoute()
const initialCategory = computed(() => {
  const q = (route.query.category as string)?.toUpperCase()
  if (!q) return 'All Goods'
  const match = products.value.find(p => p.category.toUpperCase() === q)
  return match ? toDisplayName(match.category) : 'All Goods'
})
const activeCategory = ref(initialCategory.value)

// Sync when route query changes (e.g. footer link clicked while on shop page)
watch(() => route.query.category, () => {
  activeCategory.value = initialCategory.value
})

function toDisplayName(cat: string): string {
  return cat.toLowerCase().replace(/\b\w/g, c => c.toUpperCase())
}

const uniqueCategories = computed(() =>
  [...new Set(products.value.map(p => p.category).filter(Boolean))]
)

const categoryMap = computed<Record<string, string>>(() => {
  const map: Record<string, string> = { 'All Goods': '' }
  for (const cat of uniqueCategories.value) {
    map[toDisplayName(cat)] = cat
  }
  return map
})

const categories = computed(() => ['All Goods', ...uniqueCategories.value.map(toDisplayName)])

const activeFilter = computed(() => categoryMap.value[activeCategory.value])

// ── Filtered product sets ──
const filteredProducts = computed(() =>
  activeFilter.value
    ? products.value.filter(p => p.category === activeFilter.value)
    : products.value.filter(p => p.category !== 'THE WATER')
)

const gridProducts = computed(() => filteredProducts.value)

const waterProducts = computed(() =>
  !activeFilter.value || activeFilter.value === 'THE WATER'
    ? products.value.filter(p => p.category === 'THE WATER')
    : []
)

const showCraftSection = computed(() =>
  !activeFilter.value || activeFilter.value !== 'THE WATER'
)

const isWaterOnly = computed(() => activeFilter.value === 'THE WATER')

// ── Mosaic layout slots ──
const mosaicSlots = computed<MosaicSlot[]>(() => {
  const isAllGoods = !activeFilter.value

  // Group products by category if All Goods, else single group
  let categoryGroups: { category: string; items: Product[] }[]
  if (isAllGoods) {
    const grouped = new Map<string, Product[]>()
    for (const p of gridProducts.value) {
      const existing = grouped.get(p.category) ?? []
      existing.push(p)
      grouped.set(p.category, existing)
    }
    categoryGroups = [...grouped.entries()].map(([cat, items]) => ({ category: cat, items }))
  } else {
    categoryGroups = [{ category: activeFilter.value, items: gridProducts.value }]
  }

  const slots: MosaicSlot[] = []
  let currentRow = 1
  let quoteIndex = 0
  let editorialIndex = 0
  let imageIndex = 0

  for (const group of categoryGroups) {
    // Collect all available lifestyle images for this group (prefer 3rd, then 2nd)
    const lifestyleImages: { url: string; altText: string }[] = []
    for (const p of group.items) {
      if (p.images[2]?.url) lifestyleImages.push({ url: p.images[2].url, altText: p.images[2].altText ?? '' })
      else if (p.images[1]?.url) lifestyleImages.push({ url: p.images[1].url, altText: p.images[1].altText ?? '' })
    }

    // Wavy rule + banner before each category group (All Goods only)
    if (isAllGoods) {
      const rep = group.items[0]!
      slots.push({
        type: 'wavy-rule',
        label: group.category,
        gridColumn: '1 / 7',
        gridRow: `${currentRow} / ${currentRow + 1}`,
      })
      currentRow++
      slots.push({
        type: 'banner',
        category: group.category,
        count: group.items.length,
        bgImage: rep.images[1]?.url ?? rep.images[0]?.url ?? '',
        gridColumn: '1 / 7',
        gridRow: `${currentRow} / ${currentRow + 1}`,
      })
      currentRow++
    }

    // Lay out products in repeating 3-row pattern (5 products per cycle)
    let pi = 0
    const items = group.items

    while (pi < items.length) {
      // Row A: Large product (col 1-4) + Quote (col 5-6)
      if (pi < items.length) {
        slots.push({
          type: 'product',
          product: items[pi]!,
          gridColumn: '1 / 5',
          gridRow: `${currentRow} / ${currentRow + 1}`,
          colspan: 4,
        })
        slots.push({
          type: 'quote',
          quote: QUOTES[quoteIndex % QUOTES.length]!,
          gridColumn: '5 / 7',
          gridRow: `${currentRow} / ${currentRow + 1}`,
        })
        quoteIndex++
        pi++
        currentRow++
      }

      // Row B: products + image panel
      if (pi < items.length) {
        const remaining = items.length - pi

        slots.push({
          type: 'product',
          product: items[pi]!,
          gridColumn: '1 / 3',
          gridRow: `${currentRow} / ${currentRow + 1}`,
          colspan: 2,
        })
        pi++

        const imgSource = lifestyleImages.length
          ? lifestyleImages[imageIndex % lifestyleImages.length]!
          : null

        if (remaining >= 2) {
          // 2+ products: small product col 3-4, image col 5-6
          if (pi < items.length) {
            slots.push({
              type: 'product',
              product: items[pi]!,
              gridColumn: '3 / 5',
              gridRow: `${currentRow} / ${currentRow + 1}`,
              colspan: 2,
            })
            pi++
          }

          if (imgSource) {
            slots.push({
              type: 'image',
              url: imgSource.url,
              altText: imgSource.altText,
              gridColumn: '5 / 7',
              gridRow: `${currentRow} / ${currentRow + 1}`,
            })
            imageIndex++
          } else {
            slots.push({
              type: 'editorial',
              editorial: EDITORIALS[editorialIndex % EDITORIALS.length]!,
              gridColumn: '5 / 7',
              gridRow: `${currentRow} / ${currentRow + 1}`,
            })
            editorialIndex++
          }
        } else {
          // 1 product only: wide image col 3-6
          if (imgSource) {
            slots.push({
              type: 'image',
              url: imgSource.url,
              altText: imgSource.altText,
              gridColumn: '3 / 7',
              gridRow: `${currentRow} / ${currentRow + 1}`,
            })
            imageIndex++
          } else {
            slots.push({
              type: 'editorial',
              editorial: EDITORIALS[editorialIndex % EDITORIALS.length]!,
              gridColumn: '3 / 7',
              gridRow: `${currentRow} / ${currentRow + 1}`,
            })
            editorialIndex++
          }
        }
        currentRow++
      }

      // Row C: Quote (col 1-2) + Large product (col 3-6)
      if (pi < items.length) {
        slots.push({
          type: 'quote',
          quote: QUOTES[quoteIndex % QUOTES.length]!,
          gridColumn: '1 / 3',
          gridRow: `${currentRow} / ${currentRow + 1}`,
        })
        quoteIndex++

        slots.push({
          type: 'product',
          product: items[pi]!,
          gridColumn: '3 / 7',
          gridRow: `${currentRow} / ${currentRow + 1}`,
          colspan: 4,
        })
        pi++
        currentRow++
      }
    }
  }

  return slots
})

// ── Staggered entrance observer ──
const gridRef = ref<HTMLElement | null>(null)
const visibleCards = ref<Set<number>>(new Set())
let observer: IntersectionObserver | null = null
const staggerTimers: ReturnType<typeof setTimeout>[] = []

watch(activeFilter, () => {
  visibleCards.value = new Set()
})

function setupObserver() {
  if (observer) observer.disconnect()
  staggerTimers.forEach(clearTimeout)
  staggerTimers.length = 0

  observer = new IntersectionObserver(
    (entries) => {
      const entering = entries.filter(e => e.isIntersecting)
      entering.forEach((entry, i) => {
        const idx = Number((entry.target as HTMLElement).dataset.idx)
        if (!Number.isNaN(idx)) {
          const id = setTimeout(() => {
            visibleCards.value = new Set([...visibleCards.value, idx])
          }, i * 60)
          staggerTimers.push(id)
        }
        observer?.unobserve(entry.target)
      })
    },
    { threshold: 0.1 }
  )

  nextTick(() => {
    if (!gridRef.value) return
    const cards = gridRef.value.querySelectorAll('[data-idx]')
    cards.forEach(card => observer?.observe(card))
  })
}

watch([mosaicSlots, waterProducts], () => nextTick(setupObserver), { flush: 'post' })
onMounted(setupObserver)
onUnmounted(() => {
  observer?.disconnect()
  staggerTimers.forEach(clearTimeout)
})
</script>

<template>
  <div>
    <!-- ── 1. PAGE HEADER ── -->
    <section class="relative flex items-center justify-center min-h-[360px] md:min-h-[480px] overflow-hidden">
      <img
        src="/images/products/freepik_cinematic-close-up-of-a-m_2857207265.png"
        alt=""
        class="absolute inset-0 w-full h-full object-cover"
      />
      <div class="absolute inset-0 bg-black/50" />
      <div class="relative z-10 max-w-7xl mx-auto px-4 md:px-20 flex flex-col gap-4 md:gap-4 items-center text-center py-24">
        <h1 class="font-serif font-normal text-[32px] md:text-5xl text-brand">
          Things Made <em>to Last</em>
        </h1>
        <p class="font-sans font-light text-md leading-relaxed text-white/80 max-w-prose">
          Each piece selected for its craft, material, and the way it wears over time.
        </p>
      </div>
    </section>

    <!-- ── 2. FILTER BAR ── -->
    <nav class="sticky pt-20 top-0 z-30 bg-page border-b border-stroke overflow-x-auto">
      <div class="max-w-7xl mx-auto px-4 md:px-20 flex gap-6 md:gap-8 min-w-max">
        <button
          v-for="cat in categories"
          :key="cat"
          class="font-label font-light text-xs md:text-md tracking-wider uppercase py-3 border-b-2 transition-colors whitespace-nowrap"
          :class="activeCategory === cat
            ? 'text-dark border-brand'
            : 'text-muted border-transparent hover:text-secondary'"
          @click="activeCategory = cat"
        >
          {{ cat }}
        </button>
      </div>
    </nav>

    <!-- ── 3. PRODUCT MOSAIC ── -->
    <section v-if="mosaicSlots.length && !isWaterOnly" class="bg-page py-12 md:py-16">
      <div class="max-w-7xl mx-auto px-4 md:px-20 flex flex-col gap-8 md:gap-12">
        <!-- Section label divider -->
        <!-- <div class="flex items-center gap-4">
          <p class="font-label font-light text-xs tracking-wider uppercase text-brand">
            {{ activeFilter ? toDisplayName(activeFilter) : 'All Products' }}
          </p>
          <div class="flex-1 border-b border-stroke" />
        </div> -->

        <div
          ref="gridRef"
          class="mosaic-grid"
          :style="{
            display: 'grid',
            gridTemplateColumns: 'repeat(6, 1fr)',
            gap: '1px',
            background: 'var(--color-border-default)',
          }"
        >
          <template v-for="(slot, idx) in mosaicSlots" :key="slot.type + '-' + idx">

            <!-- MOSAIC PRODUCT -->
            <div
              v-if="slot.type === 'product'"
              :data-idx="idx"
              :class="[
                'mosaic-product bg-page transition-all duration-500',
                slot.colspan === 2 ? 'mosaic-half' : '',
                !visibleCards.has(idx) ? 'opacity-0 translate-y-6' : 'opacity-100 translate-y-0',
              ]"
              :style="{
                gridColumn: slot.gridColumn,
                gridRow: slot.gridRow,
              }"
            >
              <!-- Large cell: cinematic crop (desktop only) -->
              <div
                v-if="slot.colspan === 4"
                class="hidden md:block relative h-[500px] overflow-hidden [&_img]:transition-transform [&_img]:duration-500 hover:[&_img]:scale-[1.02]"
              >
                <div class="absolute inset-0">
                  <AppProductCard
                    :images="slot.product.images"
                    :title="slot.product.name"
                    :price="slot.product.price"
                    :category="slot.product.category"
                    :handle="slot.product.handle"
                    :tags="slot.product.tags"
                    :dark="slot.dark"
                  />
                </div>
              </div>
              <!-- Standard card (mobile for large cells, always for small) -->
              <div
                :class="[
                  '[&_img]:transition-transform [&_img]:duration-500 hover:[&_img]:scale-[1.02]',
                  slot.colspan === 4 ? 'md:hidden' : '',
                ]"
              >
                <AppProductCard
                  :images="slot.product.images"
                  :title="slot.product.name"
                  :price="slot.product.price"
                  :category="slot.product.category"
                  :handle="slot.product.handle"
                  :tags="slot.product.tags"
                  :dark="slot.dark"
                />
              </div>
            </div>

            <!-- MOSAIC QUOTE -->
            <div
              v-else-if="slot.type === 'quote'"
              :data-idx="idx"
              :class="[
                'hidden md:flex relative bg-raised flex-col items-center justify-center p-8 min-h-[320px] transition-all duration-500',
                !visibleCards.has(idx) ? 'opacity-0 translate-y-6' : 'opacity-100 translate-y-0',
              ]"
              :style="{
                gridColumn: slot.gridColumn,
                gridRow: slot.gridRow,
              }"
            >
              <AppMakerStamp variant="brand" :size="90" :opacity="0.35"
                class="flex mb-10" />
              <p class="font-serif font-normal italic text-xl leading-relaxed text-navy mt-4 text-center">
                &ldquo;{{ slot.quote.text }}&rdquo;
              </p>
              <p class="font-label font-light text-xs text-muted mt-4">&mdash; {{ slot.quote.attribution }} &mdash; </p> 
              
            </div>

            <!-- MOSAIC BANNER -->
            <div
              v-else-if="slot.type === 'banner'"
              class="relative overflow-hidden h-[320px]"
              :style="{
                gridColumn: slot.gridColumn,
                gridRow: slot.gridRow,
              }"
            >
              <!-- TODO: swap src for loop video later -->
              <img
                :src="slot.bgImage"
                alt=""
                class="absolute inset-0 w-full h-full object-cover brightness-[0.4]"
              />
              <div class="absolute inset-0 bg-dark/50" />
              <div class="relative z-10 flex flex-col items-center justify-center h-full">
                <p class="font-serif font-normal text-4xl md:text-7xl text-inverse opacity-90">{{ toDisplayName(slot.category) }}</p>
                <p class="font-label font-light text-xs tracking-wider text-brand mt-2">
                  {{ slot.count }} {{ slot.count === 1 ? 'Product' : 'Products' }}
                </p>
              </div>
            </div>

            <!-- WAVY RULE after banner -->
            <div
              v-else-if="slot.type === 'wavy-rule'"
              class="bg-page"
              :style="{
                gridColumn: slot.gridColumn,
                gridRow: slot.gridRow,
              }"
            >
              <AppWavyRule :label="toDisplayName(slot.label)" />
            </div>

            <!-- MOSAIC IMAGE -->
            <div
              v-else-if="slot.type === 'image'"
              :data-idx="idx"
              :class="[
                'hidden md:block relative overflow-hidden transition-all duration-500',
                !visibleCards.has(idx) ? 'opacity-0 translate-y-6' : 'opacity-100 translate-y-0',
              ]"
              :style="{ gridColumn: slot.gridColumn, gridRow: slot.gridRow }"
            >
              <img
                :src="slot.url"
                :alt="slot.altText || ''"
                class="absolute inset-0 w-full h-full object-cover"
              />
            </div>

            <!-- MOSAIC EDITORIAL -->
            <div
              v-else-if="slot.type === 'editorial'"
              :data-idx="idx"
              :class="[
                'hidden md:flex bg-surface p-8 flex-col justify-between border-l-2 border-brand transition-all duration-500',
                !visibleCards.has(idx) ? 'opacity-0 translate-y-6' : 'opacity-100 translate-y-0',
              ]"
              :style="{
                gridColumn: slot.gridColumn,
                gridRow: slot.gridRow,
              }"
            >
              <div>
                <p class="font-label font-light text-xs tracking-wider uppercase text-brand">{{ slot.editorial.eyebrow }}</p>
                <p v-if="slot.editorial.copy" class="font-sans font-light text-sm leading-6 text-secondary mt-4">
                  {{ slot.editorial.copy }}
                </p>
              </div>
              <NuxtLink v-if="slot.editorial.cta" :to="slot.editorial.cta.href">
                <AppButton type="Ghost" size="Small" :label="slot.editorial.cta.label" :showIconRight="true" />
              </NuxtLink>
            </div>

          </template>
        </div>
      </div>
    </section>

    <!-- ── 6. WATER STRIP ── -->
    <section v-if="waterProducts.length" class="bg-surface py-12 md:py-24">
      <div class="max-w-7xl mx-auto px-4 md:px-20 flex flex-col gap-8 md:gap-12">
        <AppSectionHeader eyebrow="The Water" title="Pure &amp; Simple" />

        <div class="water-grid">
          <div
            v-for="(product, idx) in waterProducts"
            :key="product.handle"
            :data-idx="'w' + idx"
            class="bg-surface [&_img]:object-contain"
          >
            <AppProductCard
              :images="product.images"
              :title="product.name"
              :price="product.price"
              :category="product.category"
              :handle="product.handle"
              :tags="product.tags"
            />
          </div>
        </div>
      </div>
    </section>

    <!-- ── 5. CRAFT INTERSTITIAL ── -->
    <section v-if="showCraftSection" class="bg-dark py-8 md:py-10 hidden ">
      <div class="max-w-7xl mx-auto px-4 md:px-20 grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
        <!-- Quote -->
        <div class="border-l-2 border-secondary pl-4">
          <p class="font-serif font-normal italic text-sm md:text-lg leading-relaxed text-secondary">
            Every piece in The Goods is selected for durability and honesty of construction.
            We favor natural materials, aging finishes, and makers who take their time.
          </p>
        </div>

        <!-- Journal CTA -->
        <div class="flex flex-col gap-4 items-start">
          <p class="font-label font-light text-xs tracking-wider uppercase text-brand">From the Journal</p>
          <p class="font-serif font-normal text-2xl md:text-4xl text-inverse">On Materials &amp; Craft</p>
          <NuxtLink to="/journal">
            <AppButton type="Ghost" size="Small" label="Read the Story" :showIconRight="true" />
          </NuxtLink>
        </div>
      </div>
    </section>

    
  </div>
</template>

<style scoped>
@media (max-width: 767px) {
  .mosaic-grid {
    display: flex !important;
    flex-direction: column !important;
    gap: 0 !important;
    background: transparent !important;
  }
  .mosaic-grid > .mosaic-product {
    border-bottom: 0.5px solid var(--color-border-default);
  }
}

.water-grid {
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  gap: 1px;
  background: var(--color-border-default);
}

@media (min-width: 768px) {
  .water-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}
</style>
